/**
 * Light/dark preference.
 *
 * The attribute is stamped onto <html> before first paint by the inline script
 * in public/index.html, so this module only handles later changes. Token values
 * are re-declared under :root[data-theme="dark"] in tokens/vars.dark.css, so
 * flipping the attribute is all a theme change requires.
 */

const STORAGE_KEY = 'webchat_theme';

export function systemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export function getStoredTheme() {
  if (typeof document === 'undefined') return 'light';
  const attr = document.documentElement.getAttribute('data-theme');
  if (attr === 'light' || attr === 'dark') return attr;
  return systemTheme();
}

export function setStoredTheme(theme) {
  const next = theme === 'dark' ? 'dark' : 'light';
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', next);
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* Safari private mode and similar — the attribute is still applied. */
  }
  return next;
}

/**
 * Follow the OS while the learner has not expressed a preference of their own.
 * Returns an unsubscribe function.
 */
export function subscribeToSystemTheme(onChange) {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = (event) => {
    let stored = null;
    try {
      stored = window.localStorage.getItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    if (stored === 'light' || stored === 'dark') return;
    const next = event.matches ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', next);
    onChange(next);
  };
  // Safari < 14 only has the deprecated addListener.
  if (mq.addEventListener) mq.addEventListener('change', handler);
  else if (mq.addListener) mq.addListener(handler);
  return () => {
    if (mq.removeEventListener) mq.removeEventListener('change', handler);
    else if (mq.removeListener) mq.removeListener(handler);
  };
}
